import * as React from "react";

import type { SkillsController } from "../hooks/useSkillsController";
import { MAX_SELECTED_SKILLS, type SkillSummary } from "../types";
import { SkillDetailView } from "./SkillDetailView";
import { SkillExploreView } from "./SkillExploreView";

export interface SkillsViewProps {
  open: boolean;
  controller: SkillsController;
  manageSkillsUrl?: string;
  onBack: () => void;
  onClose: () => void;
  onTryInChat: (skill: SkillSummary) => void;
}

export const SkillsView: React.FC<SkillsViewProps> = ({
  open,
  controller,
  manageSkillsUrl,
  onBack,
  onClose,
  onTryInChat,
}) => {
  if (!open) return null;

  const closeView = () => {
    controller.closeDetail();
    onClose();
  };

  if (controller.detailSummary) {
    const selected = controller.selectedSkills.some(
      (skill) =>
        skill.id === controller.detailSummary?.id || skill.slug === controller.detailSummary?.slug
    );
    const atLimit = controller.selectionAtLimit && !selected;

    return (
      <SkillDetailView
        open
        skill={controller.detail}
        status={controller.detailStatus}
        selected={selected}
        tryInChatDisabled={atLimit}
        tryInChatDisabledReason={
          atLimit ? `You can select up to ${MAX_SELECTED_SKILLS} Skills.` : undefined
        }
        errorMessage={controller.detailError?.message ?? "Unable to load this Skill."}
        onBack={controller.closeDetail}
        onClose={closeView}
        onTryInChat={(skill) => {
          controller.closeDetail();
          onTryInChat(skill);
        }}
      />
    );
  }

  return (
    <SkillExploreView
      open
      skills={controller.skills}
      search={controller.browseSearch}
      sourceFilter={controller.browseSourceFilter}
      status={controller.status}
      manageSkillsUrl={manageSkillsUrl}
      errorMessage={controller.catalogError?.message ?? "Unable to load Skills."}
      scrollTop={controller.browseScrollTop}
      onScrollTopChange={controller.setBrowseScrollTop}
      onSearchChange={controller.setBrowseSearch}
      onSourceFilterChange={controller.setBrowseSourceFilter}
      onOpenDetail={controller.openDetail}
      onBack={onBack}
      onClose={closeView}
    />
  );
};
